/**
 * Utilities for splitting and assembling large data chunks
 */

import type { StorageAdapter } from './adapter';

import { CHUNK_SIZE, STORAGE_KEYS } from './constants';

export interface ChunkedDataMeta {
  chunkCount: number;
  totalSize: number;
  timestamp: number;
}

/**
 * Split string into chunks of specified size
 */
export function chunkString(data: string, chunkSize: number = CHUNK_SIZE): string[] {
  if (data.length <= chunkSize) {
    return [data];
  }

  const chunks: string[] = [];
  let offset = 0;

  while (offset < data.length) {
    chunks.push(data.slice(offset, offset + chunkSize));
    offset += chunkSize;
  }

  return chunks;
}

/**
 * Assemble chunks back into a single string
 */
export function assembleChunks(chunks: string[]): string {
  return chunks.join('');
}

/**
 * Save data with automatic chunking if needed
 */
export async function setChunkedItem(
  adapter: StorageAdapter,
  keyPrefix: string,
  data: string,
): Promise<void> {
  if (data.length <= CHUNK_SIZE) {
    // No chunking needed
    await adapter.setItem(keyPrefix, data);
    return;
  }

  // Split into chunks
  const chunks = chunkString(data, CHUNK_SIZE);
  const meta: ChunkedDataMeta = {
    chunkCount: chunks.length,
    totalSize: data.length,
    timestamp: Date.now(),
  };

  // Save metadata
  await adapter.setItem(STORAGE_KEYS.chunkMeta(keyPrefix), JSON.stringify(meta));

  // Save all chunks
  for (let i = 0; i < chunks.length; i++) {
    await adapter.setItem(STORAGE_KEYS.chunk(keyPrefix, i), chunks[i]);
  }
}

/**
 * Load data with automatic chunk assembly if needed
 */
export async function getChunkedItem(
  adapter: StorageAdapter,
  keyPrefix: string,
): Promise<string | undefined> {
  // Check if data is chunked
  const metaStr = await adapter.getItem(STORAGE_KEYS.chunkMeta(keyPrefix));

  if (!metaStr) {
    // Not chunked, read directly
    return adapter.getItem(keyPrefix);
  }

  // Parse metadata
  const meta: ChunkedDataMeta = JSON.parse(metaStr);
  const chunks: string[] = [];

  // Load all chunks
  for (let i = 0; i < meta.chunkCount; i++) {
    const chunk = await adapter.getItem(STORAGE_KEYS.chunk(keyPrefix, i));
    if (!chunk) {
      throw new Error(`Missing chunk ${i} for ${keyPrefix}`);
    }
    chunks.push(chunk);
  }

  return assembleChunks(chunks);
}

/**
 * Remove chunked data (both metadata and all chunks)
 */
export async function removeChunkedItem(
  adapter: StorageAdapter,
  keyPrefix: string,
): Promise<void> {
  // Try to get metadata first
  const metaStr = await adapter.getItem(STORAGE_KEYS.chunkMeta(keyPrefix));

  if (!metaStr) {
    // Not chunked, remove directly
    await adapter.removeItem(keyPrefix);
    return;
  }

  // Parse metadata
  const meta: ChunkedDataMeta = JSON.parse(metaStr);

  // Remove all chunks
  const keysToRemove: string[] = [STORAGE_KEYS.chunkMeta(keyPrefix)];
  for (let i = 0; i < meta.chunkCount; i++) {
    keysToRemove.push(STORAGE_KEYS.chunk(keyPrefix, i));
  }

  // Remove all keys
  for (const key of keysToRemove) {
    await adapter.removeItem(key);
  }
}
